// ─── Search Ad Validation ───────────────────────────────────────────────────
//
// Frontend validation using the config as the rules.
// Media validation is also enforced on the backend (search_media_config.py).
// Backend is authoritative (persisted in validation_result).
// Frontend provides instant feedback; aspect ratios are frontend-only.
// Returns a Record<string, FieldIssue> that flows into the existing
// fieldIssues pipeline for inline error/warning display.

import type { SearchFormatConfig } from '../search-config';
import type { SearchFormState, MediaFileItem, ImageExtension, FieldIssue } from '../shared/types';
import { SITELINK_MAX, SITELINK_TITLE_MAX, SITELINK_DESC_MAX } from '../shared/sitelinksConstants';
import { getOfferIssue, OFFER_END_DATE_FIELD_KEY } from '../../ad-details/OfferLedSection';

const RATIO_TOLERANCE = 0.05;

function matchesRatio(width: number, height: number, target: number): boolean {
  const actual = width / height;
  return Math.abs(actual - target) / target <= RATIO_TOLERANCE;
}

function formatRatio(r: number): string {
  const common: Record<string, string> = {
    '1.91': '1.91:1',
    '1.78': '16:9',
    '1.72': '1.72:1',
    '1.69': '1.69:1',
    '1.55': '1.55:1',
    '1.5': '3:2',
    '1.33': '4:3',
    '1': '1:1',
    '0.93': '0.93:1',
    '0.8': '4:5',
    '0.75': '3:4',
    '0.67': '2:3',
    '0.56': '9:16',
    '0.5': '1:2',
    '4': '4:1',
    '5': '5:1',
  };
  const key = Number(r.toFixed(2)).toString();
  return common[key] ?? `${r.toFixed(2)}:1`;
}

function isValidUrl(str: string): boolean {
  try {
    const url = new URL(str);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isValidYouTubeUrl(url: string): boolean {
  return /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/.test(url);
}

export function validateSearchForm(
  config: SearchFormatConfig,
  state: SearchFormState,
  mediaFiles: MediaFileItem[],
  imageExtensions?: ImageExtension[],
): Record<string, FieldIssue> {
  const issues: Record<string, FieldIssue> = {};
  const rules = config.validationRules;

  function addError(key: string, message: string) {
    if (!issues[key]) issues[key] = { errors: [], warnings: [] };
    issues[key].errors.push(message);
  }

  function addWarning(key: string, message: string) {
    if (!issues[key]) issues[key] = { errors: [], warnings: [] };
    issues[key].warnings.push(message);
  }

  /** Return the label in lowercase, singularised when count is 1 (e.g. "Short Headlines" → "short headline"). */
  function labelForCount(label: string, count: number): string {
    const lower = label.toLowerCase();
    return count === 1 && lower.endsWith('s') ? lower.slice(0, -1) : lower;
  }

  // ── Text field validation ───────────────────────────────────────────────

  // Headlines
  if (config.headlines) {
    const filledCount = state.headlines.filter((h) => h.trim()).length;
    if (filledCount < config.headlines.min) {
      addError(
        'headlines',
        `At least ${config.headlines.min} ${labelForCount(config.headlines.label, config.headlines.min)} required`,
      );
    }
    state.headlines.forEach((h, i) => {
      if (h.trim() && h.length > config.headlines!.charLimit) {
        addError(`headline_${i}`, `Exceeds ${config.headlines!.charLimit} character limit`);
      }
    });
    // Per-headline warning when over recommended char limit
    if (rules?.oneHeadlineMaxChars) {
      state.headlines.forEach((h, i) => {
        if (h.trim() && h.length > rules.oneHeadlineMaxChars!) {
          addWarning(`headline_${i}`, `Recommended ≤${rules.oneHeadlineMaxChars} characters`);
        }
      });
      // Section-level warning if no headline meets the recommendation
      if (filledCount > 0) {
        const hasShort = state.headlines.some(
          (h) => h.trim() && h.length <= rules.oneHeadlineMaxChars!,
        );
        if (!hasShort) {
          addWarning(
            'headlines',
            `At least one headline should be ≤${rules.oneHeadlineMaxChars} characters`,
          );
        }
      }
    }
  }

  // Long Headlines
  if (config.longHeadlines) {
    const filledLongCount = state.longHeadlines.filter((h) => h.trim()).length;
    if (filledLongCount < config.longHeadlines.min) {
      addError(
        'long_headlines',
        `At least ${config.longHeadlines.min} ${labelForCount(config.longHeadlines.label, config.longHeadlines.min)} required`,
      );
    }
    state.longHeadlines.forEach((h, i) => {
      if (h.trim() && h.length > config.longHeadlines!.charLimit) {
        addError(
          `long_headline_${i}`,
          `Exceeds ${config.longHeadlines!.charLimit} character limit`,
        );
      }
    });
    // Warning: long headline too short
    if (rules?.longHeadlineMinWarning) {
      state.longHeadlines.forEach((h, i) => {
        if (h.trim() && h.length < rules.longHeadlineMinWarning!) {
          addWarning(
            `long_headline_${i}`,
            `Recommended ≥${rules.longHeadlineMinWarning} characters`,
          );
        }
      });
    }
  }

  // Descriptions
  if (config.descriptions) {
    const filledCount = state.descriptions.filter((d) => d.trim()).length;
    if (filledCount < config.descriptions.min) {
      addError(
        'descriptions',
        `At least ${config.descriptions.min} ${labelForCount(config.descriptions.label, config.descriptions.min)} required`,
      );
    }
    state.descriptions.forEach((d, i) => {
      if (d.trim() && d.length > config.descriptions!.charLimit) {
        addError(`description_${i}`, `Exceeds ${config.descriptions!.charLimit} character limit`);
      }
    });
    // Warning: recommended description count
    if (rules?.recommendedDescriptions && filledCount < rules.recommendedDescriptions) {
      addWarning('descriptions', `Recommended ${rules.recommendedDescriptions}+ descriptions`);
    }
  }

  // Display URL Paths
  if (config.displayUrlPaths) {
    const filledPaths = state.displayUrlPaths.filter((p) => p.trim()).length;
    if (config.displayUrlPaths.min > 0 && filledPaths < config.displayUrlPaths.min) {
      addError(
        'display_url_paths',
        `At least ${config.displayUrlPaths.min} ${labelForCount(config.displayUrlPaths.label, config.displayUrlPaths.min)} required`,
      );
    }
    state.displayUrlPaths.forEach((p, i) => {
      if (p.trim() && p.length > config.displayUrlPaths!.charLimit) {
        addError(
          `display_url_path_${i}`,
          `Exceeds ${config.displayUrlPaths!.charLimit} character limit`,
        );
      }
    });
  }

  // Business Name
  if (config.businessName) {
    if (!config.businessName.optional && !state.businessName.trim()) {
      addError('business_name', 'Business name is required');
    }
    if (state.businessName.length > config.businessName.charLimit) {
      addError('business_name', `Exceeds ${config.businessName.charLimit} character limit`);
    }
  }

  // Final URL
  if (config.finalUrl) {
    if (!state.finalUrl.trim()) {
      addError('final_url', 'Final URL is required');
    } else if (!isValidUrl(state.finalUrl)) {
      addError('final_url', 'Must be a valid URL (http(s)://...)');
    }
  }

  // CTA
  if (config.cta && config.cta.min > 0) {
    const filledCtas = state.cta.filter((c) => c.trim()).length;
    if (filledCtas < config.cta.min) {
      addError('cta', `At least ${config.cta.min} CTA required`);
    }
  }

  // ── Offer-led ───────────────────────────────────────────────────────────
  // Mirrors the backend `_offer_rules` (required_if + not_in_past). The key
  // matches the backend field name so the two never double-report.

  const offerIssue = getOfferIssue(state.isOfferLed, state.offerEndDate);
  if (offerIssue) {
    addError(OFFER_END_DATE_FIELD_KEY, offerIssue);
  }

  // ── Media slot validation ───────────────────────────────────────────────
  // Media validation is also enforced on the backend (search_media_config.py).
  // Backend is authoritative (persisted in validation_result).
  // Frontend provides instant feedback; aspect ratios are frontend-only.

  if (config.mediaSlots) {
    for (const [key, slotConfig] of Object.entries(config.mediaSlots)) {
      const actualFiles = mediaFiles.filter((f) => f.slot === key && (f.file || f.saved));

      // For YouTube videos, count ones with actual URLs
      if (key === 'youtube_video') {
        const withValidUrls = mediaFiles.filter(
          (f) => f.slot === 'youtube_video' && f.url?.trim() && isValidYouTubeUrl(f.url!),
        );
        if (withValidUrls.length < slotConfig.min) {
          addError(
            `media_${key}`,
            `At least ${slotConfig.min} YouTube video${slotConfig.min > 1 ? 's' : ''} required`,
          );
        }
        // Check each YouTube URL is valid + expiration date required when URL is set
        mediaFiles
          .filter((f) => f.slot === 'youtube_video')
          .forEach((f, i) => {
            if (f.url?.trim() && !isValidYouTubeUrl(f.url)) {
              addError(`youtube_video_${i}`, 'Enter a valid YouTube URL');
            }
            if (f.url?.trim() && !f.expirationDate?.trim()) {
              addError(`youtube_video_${i}_expiration`, 'Expiration date is required');
            }
          });
      } else {
        // Images, logos, videos, banners
        if (actualFiles.length < slotConfig.min) {
          addError(
            `media_${key}`,
            `At least ${slotConfig.min} ${slotConfig.label.toLowerCase()} required`,
          );
        }
        // Recommended count warning
        if (
          slotConfig.recommendedCount &&
          actualFiles.length < slotConfig.recommendedCount &&
          actualFiles.length >= slotConfig.min
        ) {
          addWarning(
            `media_${key}`,
            `${slotConfig.recommended ?? `Recommended ${slotConfig.recommendedCount}+`}`,
          );
        }

        // Aspect ratio checking
        if (slotConfig.acceptedRatios || slotConfig.recommendedRatios) {
          const singularLabel = slotConfig.label.replace(/s$/, '');
          actualFiles.forEach((f, idx) => {
            if (!f.width || !f.height) return;
            const accepted = slotConfig.acceptedRatios;
            const recommended = slotConfig.recommendedRatios;
            const name = actualFiles.length > 1 ? `${singularLabel} ${idx + 1}` : singularLabel;
            const actual = formatRatio(f.width / f.height);

            if (accepted?.length) {
              const matchesAny = accepted.some((r) => matchesRatio(f.width!, f.height!, r));
              if (!matchesAny) {
                const expected = accepted.map(formatRatio).join(', ');
                addError(`media_ratio_${f.id}`, `${name}: ${actual} — expected ${expected}`);
              }
            }

            if (recommended?.length && !accepted?.length) {
              const matchesAny = recommended.some((r) => matchesRatio(f.width!, f.height!, r));
              if (!matchesAny) {
                const expected = recommended.map(formatRatio).join(', ');
                addWarning(`media_ratio_${f.id}`, `${name}: ${actual} — recommended ${expected}`);
              }
            } else if (recommended?.length && accepted?.length) {
              const matchesAccepted = accepted.some((r) => matchesRatio(f.width!, f.height!, r));
              if (matchesAccepted) {
                const matchesRecommended = recommended.some((r) =>
                  matchesRatio(f.width!, f.height!, r),
                );
                if (!matchesRecommended) {
                  const expected = recommended.map(formatRatio).join(', ');
                  addWarning(`media_ratio_${f.id}`, `${name}: ${actual} — recommended ${expected}`);
                }
              }
            }
          });
        }
      }
    }

    // Shared image max — total across all image types combined
    if (config.sharedImageMax) {
      const imageSlotKeys = ['image', 'landscape_image', 'square_image', 'portrait_image'];
      const totalImages = mediaFiles.filter(
        (f) => imageSlotKeys.includes(f.slot) && (f.file || f.saved),
      ).length;
      if (totalImages > config.sharedImageMax) {
        addError(
          'images_shared_max',
          `Maximum ${config.sharedImageMax} images allowed across all image types (currently ${totalImages})`,
        );
      }
    }

    // Warning: video recommended but not required
    if (rules?.videoRecommended) {
      const hasVideo = mediaFiles.some((f) => f.slot === 'youtube_video' && f.url?.trim());
      if (!hasVideo) {
        addWarning(
          'media_youtube_video',
          'Recommended to include at least 1 video for better performance',
        );
      }
    }
  }

  // ── RSA Image Extensions ────────────────────────────────────────────────

  if (imageExtensions) {
    imageExtensions.forEach((ext, i) => {
      if (ext.file || ext.saved) {
        if (!ext.expirationDate.trim()) {
          addError(
            `image_extension_${i}_expiration`,
            'Expiration date is required when an image is provided',
          );
        }
      }
    });
  }

  // ── Sitelinks (Search & PMax only) ──────────────────────────────────────
  // Optional: empty rows are skipped. Each *filled* row needs a title (≤25), a
  // valid final URL, and both description lines (each required, ≤35 chars).
  // Field keys mirror the backend (sitelinks[i].title, .final_url, …) so server
  // validation merges without duplication.

  if (config.sections.includes('sitelinks')) {
    if (state.sitelinks.length > SITELINK_MAX) {
      addError('sitelinks', `A maximum of ${SITELINK_MAX} sitelinks is allowed`);
    }
    state.sitelinks.forEach((s, i) => {
      const title = s.title.trim();
      const desc1 = s.description1.trim();
      const desc2 = s.description2.trim();
      const finalUrl = s.finalUrl.trim();
      const filled = title || desc1 || desc2 || finalUrl;
      if (!filled) return; // Skip empty rows — sitelinks are optional

      if (!title) {
        addError(`sitelinks[${i}].title`, 'Sitelink title is required');
      } else if (title.length > SITELINK_TITLE_MAX) {
        addError(
          `sitelinks[${i}].title`,
          `Sitelink title must be ${SITELINK_TITLE_MAX} characters or fewer`,
        );
      }

      if (!desc1) {
        addError(`sitelinks[${i}].description1`, 'Description line 1 is required');
      } else if (desc1.length > SITELINK_DESC_MAX) {
        addError(
          `sitelinks[${i}].description1`,
          `Description line 1 must be ${SITELINK_DESC_MAX} characters or fewer`,
        );
      }
      if (!desc2) {
        addError(`sitelinks[${i}].description2`, 'Description line 2 is required');
      } else if (desc2.length > SITELINK_DESC_MAX) {
        addError(
          `sitelinks[${i}].description2`,
          `Description line 2 must be ${SITELINK_DESC_MAX} characters or fewer`,
        );
      }

      if (!finalUrl) {
        addError(`sitelinks[${i}].final_url`, 'Sitelink final URL is required');
      } else if (!isValidUrl(finalUrl)) {
        addError(`sitelinks[${i}].final_url`, 'Must be a valid URL (http(s)://...)');
      }
    });
  }

  // ── Conditional warnings ─────────────────────────────────────────────────

  if (config.conditionalWarnings?.length) {
    const imageSlotKeys = ['image', 'landscape_image', 'square_image', 'portrait_image'];
    const hasImages = mediaFiles.some((f) => imageSlotKeys.includes(f.slot) && (f.file || f.saved));
    const hasVideos = mediaFiles.some((f) => f.slot === 'youtube_video' && f.url?.trim());
    const hasLongHeadlines = state.longHeadlines.some((h) => h.trim());
    const hasCompanionBanner = mediaFiles.some(
      (f) => f.slot === 'companion_banner' && (f.file || f.saved),
    );

    const hasNeither = !hasImages && !hasVideos;

    for (const cw of config.conditionalWarnings) {
      let conditionMet: boolean;
      switch (cw.when) {
        case 'hasNeither':
          conditionMet = hasNeither;
          break;
        case 'hasImages':
          conditionMet = hasNeither || hasImages;
          break;
        case 'hasVideos':
          conditionMet = hasNeither || hasVideos;
          break;
        case 'hasLongHeadlines':
          conditionMet = hasLongHeadlines;
          break;
        case 'hasCompanionBanner':
          conditionMet = hasCompanionBanner;
          break;
        default:
          conditionMet = false;
      }
      if (!conditionMet) continue;

      let fieldEmpty = false;
      if (cw.textField) {
        fieldEmpty = !state[cw.textField].some((v) => v.trim());
      } else if (cw.mediaSlots) {
        if (cw.mediaSlots.includes('youtube_video')) {
          fieldEmpty = !mediaFiles.some((f) => f.slot === 'youtube_video' && f.url?.trim());
        } else {
          fieldEmpty = !mediaFiles.some(
            (f) => cw.mediaSlots!.includes(f.slot) && (f.file || f.saved),
          );
        }
      }

      if (fieldEmpty) {
        addWarning(cw.warnKey, cw.message);
      }
    }
  }

  return issues;
}
